import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { broadcasters, broadcasterMetricsSummary, type Broadcaster, type BroadcasterStatus, type VerificationLevel } from '@/mocks/broadcasters';

type LoadingState = 'loading' | 'ready' | 'error';

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function simulateLoad<T>(data: T): Promise<T> {
  return new Promise((resolve, reject) => {
    const d = 600 + Math.random() * 400;
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Timeout al conectar con el servidor.'));
      } else {
        resolve(data);
      }
    }, d);
  });
}

/* ── Verification Badge ── */
const verificationBadge = (v: VerificationLevel) => {
  const map: Record<VerificationLevel, { bg: string; text: string; icon: string; label: string }> = {
    verificado: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-verified-badge-line', label: 'Verificado' },
    destacado: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-star-smile-line', label: 'Destacado' },
    ninguno: { bg: 'bg-background-100', text: 'text-foreground-400', icon: 'ri-user-line', label: 'Sin verificar' },
  };
  const st = map[v];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${st.bg} ${st.text} text-[10px] font-medium whitespace-nowrap`}>
      <i className={`${st.icon} text-[9px]`}></i>
      {st.label}
    </span>
  );
};

/* ── Status Badge ── */
const statusBadge = (s: BroadcasterStatus) => {
  const map: Record<BroadcasterStatus, { bg: string; text: string; dot: string; label: string }> = {
    activo: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Activo' },
    inactivo: { bg: 'bg-foreground-100', text: 'text-foreground-500', dot: 'bg-foreground-400', label: 'Inactivo' },
    suspendido: { bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500', label: 'Suspendido' },
  };
  const st = map[s];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${st.bg} ${st.text} text-[10px] font-medium whitespace-nowrap`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
      {st.label}
    </span>
  );
};

const platformIcons: Record<string, string> = {
  Instagram: 'ri-instagram-line',
  Twitter: 'ri-twitter-x-line',
  TikTok: 'ri-tiktok-line',
  YouTube: 'ri-youtube-line',
  Facebook: 'ri-facebook-line',
  Spotify: 'ri-spotify-line',
  LinkedIn: 'ri-linkedin-line',
  SoundCloud: 'ri-soundcloud-line',
};

const socialColors: Record<string, string> = {
  Instagram: 'text-[#E4405F]',
  Twitter: 'text-foreground-800',
  TikTok: 'text-foreground-800',
  YouTube: 'text-[#FF0000]',
  Facebook: 'text-[#1877F2]',
  Spotify: 'text-[#1DB954]',
  LinkedIn: 'text-[#0A66C2]',
  SoundCloud: 'text-[#FF5500]',
};

/* ── KPI Card ── */
function KpiCard({ label, value, icon, bg, color }: { label: string; value: string; icon: string; bg: string; color: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 hover:border-background-300 transition-colors">
      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
        <i className={`${icon} ${color} text-lg`}></i>
      </div>
      <div className="min-w-0">
        <p className="text-foreground-400 text-[10px] font-medium truncate">{label}</p>
        <p className="text-foreground-800 text-lg font-bold leading-tight">{value}</p>
      </div>
    </div>
  );
}

/* ── Filter Chip ── */
function FilterChip({ label, active, onClick, count }: { label: string; active: boolean; onClick: () => void; count: number }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-foreground-950 text-background-50'
          : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300 hover:text-foreground-700'
      }`}
    >
      {label}
      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
        active ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'
      }`}>{count}</span>
    </button>
  );
}

/* ═══════════════════════════════════════════════
   MODALS
   ═══════════════════════════════════════════════ */

/* ── View / Detail Modal ── */
function ViewBroadcasterModal({ broadcaster, onClose }: { broadcaster: Broadcaster; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-lg">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-start justify-between gap-4 z-10">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border-2 border-background-200">
              <img src={broadcaster.photoUrl} alt={broadcaster.name} className="w-full h-full object-cover" />
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm">{broadcaster.name}</h3>
              <div className="flex items-center gap-2 mt-0.5">
                {verificationBadge(broadcaster.verification)}
                {statusBadge(broadcaster.status)}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Bio */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Biografía</h4>
            <p className="text-foreground-700 text-xs leading-relaxed">{broadcaster.bio}</p>
          </div>

          {/* KPIs grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-800 text-sm font-bold">{formatNumber(broadcaster.metrics.followers)}</p>
              <p className="text-foreground-400 text-[9px] flex items-center justify-center gap-0.5 mt-0.5">
                <i className="ri-user-heart-line text-[9px]"></i>Seguidores
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-800 text-sm font-bold">{formatNumber(broadcaster.metrics.listeners)}</p>
              <p className="text-foreground-400 text-[9px] flex items-center justify-center gap-0.5 mt-0.5">
                <i className="ri-headphone-line text-[9px]"></i>Oyentes
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-800 text-sm font-bold">{broadcaster.metrics.programs}</p>
              <p className="text-foreground-400 text-[9px] flex items-center justify-center gap-0.5 mt-0.5">
                <i className="ri-mic-line text-[9px]"></i>Programas
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3 text-center">
              <p className="text-foreground-800 text-sm font-bold">{formatNumber(broadcaster.metrics.interactions)}</p>
              <p className="text-foreground-400 text-[9px] flex items-center justify-center gap-0.5 mt-0.5">
                <i className="ri-chat-1-line text-[9px]"></i>Interacciones
              </p>
            </div>
          </div>

          {/* Location & Languages */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Ubicación</p>
              <p className="text-foreground-800 text-xs flex items-center gap-1">
                <i className="ri-map-pin-line text-foreground-400 text-[10px]"></i>
                {broadcaster.city}, {broadcaster.country}
              </p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Idiomas</p>
              <p className="text-foreground-800 text-xs">{broadcaster.languages.join(' · ')}</p>
            </div>
          </div>

          {/* Specialties */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Especialidades</h4>
            <div className="flex items-center gap-1.5 flex-wrap">
              {broadcaster.specialties.map((s) => (
                <span key={s} className="px-2 py-1 rounded-full bg-background-100 text-foreground-600 text-[10px] font-medium">
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Programas</h4>
            <div className="space-y-2">
              {broadcaster.programs.map((p) => (
                <div key={p.id} className="bg-background-100 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground-800 text-xs font-semibold">{p.name}</p>
                    <p className="text-foreground-400 text-[9px] mt-0.5">
                      {p.radioName} · {p.radioFrequency}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-foreground-500 text-[10px] flex items-center gap-1">
                      <i className="ri-time-line text-[9px]"></i>
                      {p.schedule}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 text-[9px] font-medium">{p.role}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social Networks */}
          <div>
            <h4 className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide mb-2">Redes Sociales</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {broadcaster.socials.map((s) => (
                <div key={s.platform} className="bg-background-100 rounded-xl p-2.5 text-center">
                  <i className={`${platformIcons[s.platform] ?? 'ri-share-line'} ${socialColors[s.platform] ?? 'text-foreground-400'} text-lg`}></i>
                  <p className="text-foreground-800 text-xs font-semibold mt-1">{formatNumber(s.followers)}</p>
                  <p className="text-foreground-400 text-[9px]">{s.platform}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Joined date */}
          <div className="flex items-center gap-2 text-foreground-400 text-[10px]">
            <i className="ri-calendar-line text-[10px]"></i>
            <span>En RFW desde {broadcaster.joinedDate}</span>
          </div>
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Broadcaster Modal ── */
function EditBroadcasterModal({ broadcaster, onClose, onSave }: { broadcaster: Broadcaster; onClose: () => void; onSave: (data: Partial<Broadcaster>) => void }) {
  const [formData, setFormData] = useState({
    name: broadcaster.name,
    city: broadcaster.city,
    country: broadcaster.country,
    status: broadcaster.status,
    verification: broadcaster.verification,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = () => {
    setSaving(true);
    setTimeout(() => {
      onSave(formData);
      setSaving(false);
      onClose();
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-lg">
        <div className="sticky top-0 bg-background-50 border-b border-background-200 p-5 flex items-center justify-between gap-4 z-10">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Editar Locutor</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">{broadcaster.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0 cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Nombre</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Ciudad</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
            <div>
              <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">País</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData((p) => ({ ...p, country: e.target.value }))}
                className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors"
              />
            </div>
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Estado</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value as BroadcasterStatus }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="suspendido">Suspendido</option>
            </select>
          </div>
          <div>
            <label className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wide block mb-1">Verificación</label>
            <select
              value={formData.verification}
              onChange={(e) => setFormData((p) => ({ ...p, verification: e.target.value as VerificationLevel }))}
              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer"
            >
              <option value="verificado">Verificado</option>
              <option value="destacado">Destacado</option>
              <option value="ninguno">Sin verificar</option>
            </select>
          </div>
        </div>

        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 p-4 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium cursor-pointer hover:bg-background-200 transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name.trim()}
            className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Broadcaster Card ── */
function BroadcasterCard({ broadcaster, onView, onEdit }: { broadcaster: Broadcaster; onView: (b: Broadcaster) => void; onEdit: (b: Broadcaster) => void }) {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-all group space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border-2 border-background-200">
          <img src={broadcaster.photoUrl} alt={broadcaster.name} className="w-full h-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="text-foreground-800 text-xs font-semibold line-clamp-1">{broadcaster.name}</p>
            {broadcaster.verification === 'verificado' && (
              <i className="ri-verified-badge-line text-emerald-500 text-sm shrink-0"></i>
            )}
            {broadcaster.verification === 'destacado' && (
              <i className="ri-star-smile-line text-amber-500 text-sm shrink-0"></i>
            )}
          </div>
          <p className="text-foreground-400 text-[9px] mt-0.5 flex items-center gap-1">
            <i className="ri-map-pin-line text-[8px]"></i>
            {broadcaster.city}, {broadcaster.country}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            {statusBadge(broadcaster.status)}
            {verificationBadge(broadcaster.verification)}
          </div>
        </div>
      </div>

      {/* Bio excerpt */}
      <p className="text-foreground-500 text-[10px] leading-relaxed line-clamp-2">{broadcaster.bio}</p>

      {/* Social icons */}
      <div className="flex items-center gap-1.5">
        {broadcaster.socials.slice(0, 4).map((s) => (
          <div key={s.platform} className="w-6 h-6 rounded-md bg-background-100 flex items-center justify-center" title={`${s.platform}: ${formatNumber(s.followers)}`}>
            <i className={`${platformIcons[s.platform] ?? 'ri-share-line'} ${socialColors[s.platform] ?? 'text-foreground-400'} text-[10px]`}></i>
          </div>
        ))}
        {broadcaster.socials.length > 4 && (
          <span className="text-foreground-400 text-[9px]">+{broadcaster.socials.length - 4}</span>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-1.5">
        <div className="bg-background-100 rounded-lg p-2 text-center">
          <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(broadcaster.metrics.followers)}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
            <i className="ri-user-heart-line text-[7px]"></i>Seg.
          </p>
        </div>
        <div className="bg-background-100 rounded-lg p-2 text-center">
          <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(broadcaster.metrics.listeners)}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
            <i className="ri-headphone-line text-[7px]"></i>Oyen.
          </p>
        </div>
        <div className="bg-background-100 rounded-lg p-2 text-center">
          <p className="text-foreground-800 text-[11px] font-bold">{broadcaster.metrics.programs}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
            <i className="ri-mic-line text-[7px]"></i>Prog.
          </p>
        </div>
        <div className="bg-background-100 rounded-lg p-2 text-center">
          <p className="text-foreground-800 text-[11px] font-bold">{formatNumber(broadcaster.metrics.interactions)}</p>
          <p className="text-foreground-400 text-[7px] flex items-center justify-center gap-0.5">
            <i className="ri-chat-1-line text-[7px]"></i>Int.
          </p>
        </div>
      </div>

      {/* Footer + Actions */}
      <div className="flex items-center justify-between pt-1 border-t border-background-100">
        <span className="text-foreground-400 text-[9px] flex items-center gap-1">
          <i className="ri-mic-line text-[9px]"></i>
          {broadcaster.programs.length} programa{broadcaster.programs.length !== 1 ? 's' : ''}
        </span>
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className={`${showActions ? 'ri-close-line' : 'ri-more-2-line'} text-foreground-500 text-xs`}></i>
          </button>
          {showActions && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowActions(false)}></div>
              <div className="absolute right-0 bottom-full mb-1 z-20 bg-background-50 border border-background-200 rounded-xl shadow-lg py-1 min-w-[120px]">
                <button onClick={() => { setShowActions(false); onView(broadcaster); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-eye-line text-sm"></i>Ver perfil
                </button>
                <button onClick={() => { setShowActions(false); onEdit(broadcaster); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-edit-line text-sm"></i>Editar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Loading Skeleton ── */
function LoadingSkeleton() {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 rounded-xl bg-background-200"></div>
            <div className="space-y-1">
              <div className="w-16 h-2.5 bg-background-200 rounded"></div>
              <div className="w-10 h-4 bg-background-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 space-y-3 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-background-200"></div>
              <div className="space-y-1 flex-1">
                <div className="w-2/3 h-3 bg-background-200 rounded"></div>
                <div className="w-1/3 h-2 bg-background-200 rounded"></div>
              </div>
            </div>
            <div className="w-full h-6 bg-background-200 rounded"></div>
            <div className="grid grid-cols-4 gap-1.5">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="h-10 bg-background-200 rounded-lg"></div>
              ))}
            </div>
            <div className="flex justify-between">
              <div className="w-16 h-2.5 bg-background-200 rounded"></div>
              <div className="w-7 h-7 bg-background-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Error State ── */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
        <i className="ri-error-warning-line text-red-400 text-2xl"></i>
      </div>
      <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar</p>
      <p className="text-foreground-400 text-xs mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line text-sm"></i>
        Reintentar
      </button>
    </div>
  );
}

/* ── Empty State ── */
function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
        <i className="ri-user-voice-line text-foreground-300 text-xl"></i>
      </div>
      <p className="text-foreground-500 text-sm font-medium">Sin resultados</p>
      <p className="text-foreground-300 text-xs mt-1">No hay locutores que coincidan con los filtros</p>
      <button onClick={onClear} className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer">
        <i className="ri-filter-off-line text-xs"></i>
        Limpiar filtros
      </button>
    </div>
  );
}

/* ── Summary Bar ── */
function SummaryBar() {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-3.5 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
          <i className="ri-user-voice-line text-primary-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Total Locutores</p>
          <p className="text-foreground-800 text-sm font-bold">{broadcasterMetricsSummary.totalBroadcasters}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
          <i className="ri-user-heart-line text-emerald-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Seguidores Totales</p>
          <p className="text-foreground-800 text-sm font-bold">{formatNumber(broadcasterMetricsSummary.totalFollowers)}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <i className="ri-headphone-line text-amber-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Oyentes Totales</p>
          <p className="text-foreground-800 text-sm font-bold">{formatNumber(broadcasterMetricsSummary.totalListeners)}</p>
        </div>
      </div>
      <div className="w-px h-8 bg-background-200 hidden sm:block"></div>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
          <i className="ri-mic-line text-violet-500 text-sm"></i>
        </div>
        <div>
          <p className="text-foreground-400 text-[9px]">Programas</p>
          <p className="text-foreground-800 text-sm font-bold">{broadcasterMetricsSummary.totalPrograms}</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════ */

export default function SABroadcasters() {
  const [broadcasterList, setBroadcasterList] = useState<Broadcaster[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<BroadcasterStatus | 'todos'>('todos');
  const [verifyFilter, setVerifyFilter] = useState<VerificationLevel | 'todos'>('todos');
  const [searchTerm, setSearchTerm] = useState('');

  const [viewBroadcaster, setViewBroadcaster] = useState<Broadcaster | null>(null);
  const [editBroadcaster, setEditBroadcaster] = useState<Broadcaster | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad(broadcasters)
      .then((data) => {
        setBroadcasterList(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredBroadcasters = useMemo(() => {
    let result = broadcasterList;
    if (statusFilter !== 'todos') {
      result = result.filter((b) => b.status === statusFilter);
    }
    if (verifyFilter !== 'todos') {
      result = result.filter((b) => b.verification === verifyFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (b) =>
          b.name.toLowerCase().includes(q) ||
          b.city.toLowerCase().includes(q) ||
          b.country.toLowerCase().includes(q) ||
          b.specialties.some((s) => s.toLowerCase().includes(q)) ||
          b.programs.some((p) => p.name.toLowerCase().includes(q)),
      );
    }
    return result;
  }, [broadcasterList, statusFilter, verifyFilter, searchTerm]);

  /* Counts */
  const statusCounts = useMemo(() => ({
    todos: broadcasterList.length,
    activo: broadcasterList.filter((b) => b.status === 'activo').length,
    suspendido: broadcasterList.filter((b) => b.status === 'suspendido').length,
  }), [broadcasterList]);

  const verifyCounts = useMemo(() => ({
    todos: broadcasterList.length,
    verificado: broadcasterList.filter((b) => b.verification === 'verificado').length,
    destacado: broadcasterList.filter((b) => b.verification === 'destacado').length,
    ninguno: broadcasterList.filter((b) => b.verification === 'ninguno').length,
  }), [broadcasterList]);

  const filteredMetrics = useMemo(() => ({
    total: filteredBroadcasters.length,
    followers: filteredBroadcasters.reduce((s, b) => s + b.metrics.followers, 0),
    listeners: filteredBroadcasters.reduce((s, b) => s + b.metrics.listeners, 0),
    programs: filteredBroadcasters.reduce((s, b) => s + b.metrics.programs, 0),
    interactions: filteredBroadcasters.reduce((s, b) => s + b.metrics.interactions, 0),
  }), [filteredBroadcasters]);

  const handleEditSave = useCallback((data: Partial<Broadcaster>) => {
    if (!editBroadcaster) return;
    setBroadcasterList((prev) =>
      prev.map((b) => (b.id === editBroadcaster.id ? { ...b, ...data } : b))
    );
  }, [editBroadcaster]);

  const clearAllFilters = () => {
    setStatusFilter('todos');
    setVerifyFilter('todos');
    setSearchTerm('');
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Locutores</span>
            </div>

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[10px] font-semibold mb-2">
                  <i className="ri-user-voice-line text-xs"></i>
                  Bloque SA-51
                </div>
                <h2 className="text-foreground-950 font-bold text-lg">Locutores</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Gestión de locutores como entidad principal · {loadingState === 'ready' ? `${broadcasterList.length} locutores` : 'Cargando...'}
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar locutor, ciudad, programa..."
                  disabled={loadingState !== 'ready'}
                  className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"
                  >
                    <i className="ri-close-line text-foreground-500 text-[10px]"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Summary Bar */}
            {loadingState === 'ready' && <SummaryBar />}

            {/* KPIs */}
            {loadingState === 'ready' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                <KpiCard label="Seguidores" value={formatNumber(filteredMetrics.followers)} icon="ri-user-heart-line" bg="bg-primary-50" color="text-primary-500" />
                <KpiCard label="Oyentes" value={formatNumber(filteredMetrics.listeners)} icon="ri-headphone-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Programas" value={String(filteredMetrics.programs)} icon="ri-mic-line" bg="bg-amber-50" color="text-amber-500" />
                <KpiCard label="Interacciones" value={formatNumber(filteredMetrics.interactions)} icon="ri-chat-1-line" bg="bg-violet-50" color="text-violet-500" />
                <KpiCard label="Verificados" value={String(broadcasterMetricsSummary.verifiedCount)} icon="ri-verified-badge-line" bg="bg-emerald-50" color="text-emerald-500" />
                <KpiCard label="Destacados" value={String(broadcasterMetricsSummary.featuredCount)} icon="ri-star-smile-line" bg="bg-amber-50" color="text-amber-500" />
              </div>
            )}

            {/* Loading KPIs */}
            {loadingState === 'loading' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 flex items-center gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-xl bg-background-200"></div>
                    <div className="space-y-1">
                      <div className="w-16 h-2.5 bg-background-200 rounded"></div>
                      <div className="w-10 h-4 bg-background-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-foreground-400 text-[9px] font-medium mr-1">Estado:</span>
                  <FilterChip label="Todos" active={statusFilter === 'todos'} onClick={() => setStatusFilter('todos')} count={statusCounts.todos} />
                  <FilterChip label="Activos" active={statusFilter === 'activo'} onClick={() => setStatusFilter('activo')} count={statusCounts.activo} />
                  <FilterChip label="Suspendidos" active={statusFilter === 'suspendido'} onClick={() => setStatusFilter('suspendido')} count={statusCounts.suspendido} />
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-foreground-400 text-[9px] font-medium mr-1">Verificación:</span>
                  <button
                    onClick={() => setVerifyFilter('todos')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      verifyFilter === 'todos' ? 'bg-foreground-950 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    Todos
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${verifyFilter === 'todos' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{verifyCounts.todos}</span>
                  </button>
                  <button
                    onClick={() => setVerifyFilter('verificado')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      verifyFilter === 'verificado' ? 'bg-emerald-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-emerald-300'
                    }`}
                  >
                    Verificados
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${verifyFilter === 'verificado' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{verifyCounts.verificado}</span>
                  </button>
                  <button
                    onClick={() => setVerifyFilter('destacado')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      verifyFilter === 'destacado' ? 'bg-amber-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-amber-300'
                    }`}
                  >
                    Destacados
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${verifyFilter === 'destacado' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{verifyCounts.destacado}</span>
                  </button>
                  <button
                    onClick={() => setVerifyFilter('ninguno')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      verifyFilter === 'ninguno' ? 'bg-foreground-600 text-background-50' : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    Sin Verificar
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${verifyFilter === 'ninguno' ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-400'}`}>{verifyCounts.ninguno}</span>
                  </button>
                </div>
              </div>
            )}

            {/* Cards Grid */}
            {loadingState === 'loading' && <LoadingSkeleton />}

            {loadingState === 'error' && <ErrorState message={errorMsg || 'Error'} onRetry={loadData} />}

            {loadingState === 'ready' && filteredBroadcasters.length === 0 && (
              <EmptyState onClear={clearAllFilters} />
            )}

            {loadingState === 'ready' && filteredBroadcasters.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredBroadcasters.map((broadcaster) => (
                  <BroadcasterCard
                    key={broadcaster.id}
                    broadcaster={broadcaster}
                    onView={setViewBroadcaster}
                    onEdit={setEditBroadcaster}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Locutores</p>
      </div>

      {/* Modals */}
      {viewBroadcaster && <ViewBroadcasterModal broadcaster={viewBroadcaster} onClose={() => setViewBroadcaster(null)} />}
      {editBroadcaster && <EditBroadcasterModal broadcaster={editBroadcaster} onClose={() => setEditBroadcaster(null)} onSave={handleEditSave} />}
    </div>
  );
}